import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Waypoint } from 'react-waypoint';
import UAParser from 'ua-parser-js';

import { useTrackClickContent } from '@/modules/analytic/hooks/useTrackClickContent';
import { useTrackViewContent } from '@/modules/analytic/hooks/useTrackViewContent';
import { Block, Media, Program } from '@/types';
import { getUrlExtension } from '@/utils';

const UAString = new UAParser();

const isSafari = UAString.getBrowser()?.name?.includes('Safari') || false;

export interface BlockProgramProps {
  program: Program;
  media?: Media;
  showTitle?: boolean;
  showKeyword?: boolean;
  showPremium?: boolean;
  radius?: number;
  shadow?: boolean;
  block?: Block;
}

export const BlockProgram = ({
  program,
  media,
  showTitle,
  showKeyword,
  block,
}: BlockProgramProps) => {
  const { trackViewContent } = useTrackViewContent();
  const { trackClickContent } = useTrackClickContent();
  const { formatMessage } = useIntl();
  const router = useRouter();

  const cover = useMemo(() => media?.cover || program.cover, [media?.cover, program.cover]);

  const imageFormat = cover && getUrlExtension(cover);

  const handleBlockViewed = () => {
    if (!block) return;

    trackViewContent({ block, program });
  };

  const handleBlockClicked = useCallback(() => {
    router.push(program.route);
    trackClickContent({ block, program });
  }, [block, program, router, trackClickContent]);

  /** @todo: use css instead of png placeholders */
  const programSrcset = useMemo(() => {
    [16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840]
      .map((w) => `${cover}?w=${w}&auto=format&fm=${imageFormat} ${w}w`)
      .join(',');
  }, [cover, imageFormat]);

  return (
    <button
      onClick={handleBlockClicked}
      aria-label={formatMessage(
        { id: 'catalogScreen.accessibility.program.link' },
        { programTitle: program.title }
      )}
      className="w-full"
    >
      <Waypoint onEnter={handleBlockViewed} />
      <div className="img-square">
        {cover ? (
          isSafari ? (
            <Image
              src={`${cover}?q=10&w=50`}
              program-src={`${cover}?auto=format&fm=${imageFormat}`}
              program-srcset={programSrcset}
              fill
              alt={program.title}
              className="lazyload"
            />
          ) : (
            <Image
              src={cover}
              fill
              alt={program.title}
              className={classNames(
                "bg-[url('/logo-majelan-m.png')] bg-center bg-no-repeat bg-[length:25px_25px] bg-opacity-20",
                { 'p-8': media?.type === 'audio' }
              )}
              loading="lazy"
            />
          )
        ) : (
          <Image
            src="/logo-majelan-m.png?q=10&w=25"
            program-src={`/logo-majelan-m.png?auto=format&fm=${imageFormat}`}
            width={25}
            height={25}
            alt={program.title}
            className="bg-[url('/logo-majelan-m.png')] bg-center bg-no-repeat bg-[length:25px_25px] bg-opacity-20"
          />
        )}
      </div>
      {showKeyword && showTitle && (
        <div className="pt-3 pb-1.5 px-1.5 hidden lg:flex flex-col flex-start text-start">
          {showKeyword && !!program.keyword1 && (
            <div className="flex flex-row flex-start items-center">
              <span className="text-brand-neutral-300 font-medium leading-5 text-[14px] tracking-[0.4px]">
                {program.isCorporate
                  ? program.keyword1
                  : formatMessage({ id: `common.format.${program.keyword1}` })}
              </span>
            </div>
          )}
          {showTitle && (
            <h6
              aria-label="titre du programme"
              className="text-[14px] leading-[1.47rem] tracking-[0.4px] text-white font-light my-[3px] mx-0"
            >
              {program.title}
            </h6>
          )}
        </div>
      )}
    </button>
  );
};
